// GameStateStore — the single source of truth (AGENT.md §1). Every mutation
// goes through mutate() so persistence and subscribers can never drift from it.

import { bus } from './events';
import { createSaveAdapter, type SaveAdapter } from './persistence';
import { createInitialState, SCHEMA_VERSION } from './initialState';
import type { GameState } from './types';
import { performAction } from '../sim/actionLog';
import type { ActionResult, SimAction } from '../sim/actions';

export type StateListener = (state: GameState) => void;

/**
 * Migrates an older save forward. Older-but-readable saves are merged over a
 * fresh state so fields added since they were written get defaults while
 * everything the player earned survives; only a save from a *newer* schema
 (which this build cannot understand) resets, mirroring the
 * FutureSaveVersionError policy on the persistence layer.
 */
export function migrate(state: GameState): GameState {
  if (state.schemaVersion === SCHEMA_VERSION) return state;
  const fresh = createInitialState(state.seed);
  if (typeof state.schemaVersion !== 'number' || state.schemaVersion > SCHEMA_VERSION) {
    console.warn(
      `[store] save schema v${String(state.schemaVersion)} unreadable by v${SCHEMA_VERSION}; starting fresh`,
    );
    return fresh;
  }
  console.warn(`[store] migrating save schema v${state.schemaVersion} -> v${SCHEMA_VERSION}`);
  return { ...fresh, ...state, schemaVersion: SCHEMA_VERSION };
}

export class GameStateStore {
  private state: GameState;
  private adapter: SaveAdapter | null = null;
  private listeners = new Set<StateListener>();
  private autosaveTimer: ReturnType<typeof setInterval> | null = null;
  private dirty = false;
  /** Set when init() threw a pre-refocus save away. Read once, by the notice. */
  private resetNoticePending = false;

  constructor(state: GameState = createInitialState()) {
    this.state = state;
  }

  /** Loads the persisted save, or seeds a new one if there is none. */
  async init(): Promise<GameState> {
    this.adapter = await createSaveAdapter();
    const loaded = await this.adapter.load();
    // A save that was too old to keep is not the same as no save at all: the
    // player had a farm and no longer does, and the plan asks for one friendly
    // notice saying so. Brand-new players get none.
    this.resetNoticePending = loaded === null && this.adapter.lastLoadWasStale;
    this.state = loaded ? migrate(loaded) : createInitialState();
    bus.emit('STATE_LOADED', { state: this.state });
    this.notify();
    return this.state;
  }

  /**
   * True exactly once after a boot that discarded a pre-refocus save, so the
   * notice fires on arrival rather than on every later frame.
   */
  consumeResetNotice(): boolean {
    if (!this.resetNoticePending) return false;
    this.resetNoticePending = false;
    return true;
  }

  getState(): Readonly<GameState> {
    return this.state;
  }

  /** Deep copy — safe to hand to the dev dashboard or a test. */
  getRawState(): GameState {
    return structuredClone(this.state) as GameState;
  }

  /** The only write path. Mutate in place; the store handles the rest. */
  mutate(fn: (state: GameState) => void): void {
    fn(this.state);
    this.dirty = true;
    this.notify();
  }

  /**
   * The write path for anything the PLAYER does (PLAN-04 S1). It goes through
   * the sim's `performAction`, which is where Veteran appends to the action log
   * the server replays — so a screen cannot change the world without it being
   * recorded. `mutate` stays for the engine, loading and dev tools.
   */
  dispatch(action: SimAction): ActionResult {
    let result: ActionResult = { performed: false, messageKey: null };
    this.mutate((state) => {
      result = performAction(state, action);
    });
    return result;
  }

  /** Replaces state wholesale (load, import, dev override). */
  replace(state: GameState): void {
    this.state = migrate(state);
    this.dirty = true;
    bus.emit('STATE_LOADED', { state: this.state });
    this.notify();
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of [...this.listeners]) {
      try {
        listener(this.state);
      } catch (err) {
        console.error('[store] listener threw', err);
      }
    }
  }

  async save(): Promise<void> {
    if (!this.adapter) return;
    this.state.lastSavedTimestamp = Date.now();
    await this.adapter.save(this.state);
    this.dirty = false;
    bus.emit('STATE_SAVED', { timestamp: this.state.lastSavedTimestamp });
  }

  /** Writes only when something actually changed since the last save. */
  startAutosave(intervalMs = 10_000): void {
    this.stopAutosave();
    this.autosaveTimer = setInterval(() => {
      if (this.dirty) void this.save();
    }, intervalMs);
  }

  stopAutosave(): void {
    if (this.autosaveTimer !== null) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
  }

  async reset(): Promise<GameState> {
    await this.adapter?.clear();
    this.replace(createInitialState());
    return this.state;
  }

  // --- Developer export/import (AGENT.md §1, DEV-DASHBOARD.md §3) ---

  exportJson(): string {
    return JSON.stringify(this.getRawState(), null, 2);
  }

  importJson(json: string): void {
    const parsed = JSON.parse(json) as GameState;
    if (typeof parsed?.schemaVersion !== 'number' || !Array.isArray(parsed.flock)) {
      throw new Error('Not a Clay Farm save: missing schemaVersion or flock');
    }
    this.replace(parsed);
  }
}
